import { useMemo } from 'react';
import cls from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useStakingStore } from '@/store/staking';
import { Event, Page } from '@/config/track-config';
import { scTrack } from '@web3/base-tracking';
import { BannerItem } from '@/types/staking';
import { Navigation } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import styles from './index.module.less';

import ArrowIcon from '~/public/svg/home/arrow.svg';
import PixelArrowIcon from '~/public/svg/home/pixel-arrow.svg';

export const FIX_COUNT = 4;
export const MIN_COUNT = 1;

export const Carousel = () => {
  const { configLoading, homeConfig } = useStakingStore();

  // 过滤没有webJumpLink的banner
  const bannerList = (homeConfig.bannerList || []).filter(
    (item) => !!item.webJumpLink
  );

  // 唯一数据 - 单独处理样式
  const isOnlyOneData = useMemo(() => {
    return bannerList?.length === MIN_COUNT;
  }, [bannerList?.length]);

  // 超过4个展示轮播和箭头
  const isShowArrow = useMemo(() => {
    return bannerList?.length >= FIX_COUNT;
  }, [bannerList?.length]);

  const handleClick = (item: BannerItem) => {
    // 埋点: Earn_Events - 轮播图 - 4图
    // 埋点: Earn_Eventsimportance - 轮播图 1图
    scTrack(Event.Web3UserClick, {
      page_name: Page.StakingWebHomepage,
      button_name: isOnlyOneData ? 'Earn_Eventsimportance' : 'Earn_Events',
    });

    if (item.webJumpLink) {
      window.top?.open(item.webJumpLink, '_self');
    }
  };

  if (!configLoading && !bannerList?.length) return <></>;

  return (
    <div className={styles.container}>
      {!!bannerList?.length && (
        <div
          className={cls('relative', styles.contentBg, {
            [styles.largeContentBg]: isOnlyOneData,
          })}
        >
          <Swiper
            key="staking-banner"
            effect="coverflow"
            loop={isShowArrow}
            slidesPerView={MIN_COUNT}
            spaceBetween={24}
            modules={[Navigation]}
            pagination={false}
            navigation={
              isShowArrow
                ? {
                    nextEl: '.arrow-left',
                    prevEl: '.arrow-right',
                  }
                : false
            }
          >
            {/* 第一页 展示 1,2,3,4 */}
            <SwiperSlide>
              <div className={cls(styles.slideBg)}>
                {(bannerList?.slice(0, FIX_COUNT) || []).map(
                  (item: BannerItem) => {
                    const { title, describe, webIcon } = item;

                    return (
                      <div
                        key={title}
                        className={cls(
                          'flex justify-between cursor-pointer',
                          styles.box,
                          {
                            [styles.largeBox]: isOnlyOneData,
                          }
                        )}
                        onClick={() => handleClick(item)}
                      >
                        <img
                          src={webIcon}
                          className={cls('object-cover', styles.img, {
                            [styles.largeImg]: isOnlyOneData,
                          })}
                          alt=""
                        />

                        <div
                          className={cls(styles.content, {
                            [styles.largeContent]: isOnlyOneData,
                          })}
                        >
                          <div
                            className={cls(styles.title, {
                              [styles.largeTitle]: isOnlyOneData,
                            })}
                          >
                            {title}
                          </div>
                          <p
                            className={cls(styles.desc, {
                              [styles.largeDesc]: isOnlyOneData,
                            })}
                          >
                            {describe}
                          </p>
                          {/* 仅只有一条数据的时候, 展示箭头*/}
                          {isOnlyOneData && (
                            <ArrowIcon
                              className={cls('object-cover mt-4', styles.icon)}
                              style={{
                                height: 32,
                                width: 32,
                              }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </SwiperSlide>

            {/* 第二页 展示 4,5,6 */}
            <SwiperSlide>
              <div className={cls(styles.slideBg)}>
                {(
                  bannerList?.slice(FIX_COUNT - 1, FIX_COUNT * 2 - 2) || []
                ).map((item: BannerItem) => {
                  const { title, describe, webIcon } = item;

                  return (
                    <div
                      key={`${title}2`}
                      className={cls(
                        'flex justify-between cursor-pointer',
                        styles.box
                      )}
                      onClick={() => handleClick(item)}
                    >
                      <img
                        src={webIcon}
                        className={cls('object-cover', styles.img)}
                        alt=""
                      />

                      <div
                        className={cls(styles.content, {
                          [styles.largeContent]: isOnlyOneData,
                        })}
                      >
                        <div
                          className={cls(styles.title, {
                            [styles.largeTitle]: isOnlyOneData,
                          })}
                        >
                          {title}
                        </div>
                        <p
                          className={cls(styles.desc, {
                            [styles.largeDesc]: isOnlyOneData,
                          })}
                        >
                          {describe}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </SwiperSlide>
          </Swiper>
          {/* 箭头 */}
          {isShowArrow && (
            <>
              <button
                className={cls(
                  'arrow-left',
                  'flex justify-center items-center absolute',
                  styles.prevBtn
                )}
              >
                <PixelArrowIcon
                  className={styles.pixelLfArrow}
                  style={{
                    height: 20,
                    width: 20,
                  }}
                />
              </button>
              <button
                className={cls(
                  'arrow-right',
                  'flex justify-center items-center absolute',
                  styles.nextBtn
                )}
              >
                <PixelArrowIcon
                  className={styles.pixelRTArrow}
                  style={{
                    height: 20,
                    width: 20,
                  }}
                />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
